/**
 * Signals drawn on the price chart: types (mirror backend/src/services/
 * chartSignals.ts), the plain-English words for each outcome, the pure
 * helpers behind the overlay and legend, and the fetch hook.
 *
 * A chart signal is the 1h engine's trade idea at the candle it fired on:
 * BUY/SELL tag, entry, stop, targets, and what price did next, so it can be
 * checked against the candles by eye.
 */

import { useEffect, useState } from "react";

// ─── types (mirror backend/src/services/chartSignals.ts) ─────────────────────

export type ChartSignalResult =
  | "pending" | "no-fill" | "open" | "stopped" | "be-stop" | "full-win" | "time-stop" | "closed";

export interface ChartSignal {
  id: string;
  /** "live" = the desk's Ready signal on the candle that just closed; "mine" = a trade the user logged. */
  source: "telegram" | "replay" | "live" | "mine";
  action: "LONG" | "SHORT";
  /** Unix second the 1h trigger candle closed: the moment the signal fired. */
  signalTime: number;
  entry: number; stopLoss: number; tp1: number; tp2: number; riskReward: number;
  /** Don't-chase price (entry ± CHASE_ATR × ATR): past it, skip the entry. Null without an ATR. */
  chase?: number | null;
  archetype: string | null; quality: string | null; trigger: string | null;
  thesis: string | null; score: number | null; timeStopHours: number | null;
  result: ChartSignalResult;
  fillTime: number | null; tp1Time: number | null; exitTime: number | null; exitPrice: number | null;
  /** Net R (after fees): realised when closed, at the last price while open. */
  r: number | null;
  /** Against BTC's trend: the engine sent it at half risk (or you sized your trade at half). */
  againstBtc?: boolean;
}

export interface ChartSignalsResponse {
  success: boolean; error?: string;
  symbol: string; signalTf: string; days: number; generatedAt: number;
  signals: ChartSignal[];
}

/** The plan of a signal the desk shows right now (its Ready card). */
export type LivePlan = Pick<ChartSignal,
  "action" | "entry" | "stopLoss" | "tp1" | "tp2" | "riskReward" | "chase" | "archetype" | "quality" | "trigger" | "thesis" | "score" | "timeStopHours" | "againstBtc">;

/** Don't chase: skip the entry once price ran this many ATR past it (backend openSignalStatus.ts CHASE_ATR). */
export const CHASE_ATR = 0.3;
/** The entry window: two 1h candles after the signal, then it is stale (backend ENTRY_WINDOW_MS). */
export const ENTRY_WINDOW_SEC = 2 * 3600;

/** The desk's answer for the candle it last checked: a plan, or null for "no trade". */
export interface DeskCheck { closeSec: number; plan: LivePlan | null }

/**
 * What a coin is waiting for (the desk checklist, stage Waiting or Almost):
 * the zone a trade would start in and where its stop would go.
 */
export interface SetupZone {
  direction: "LONG" | "SHORT";
  low: number;
  high: number;
  stop: number | null;
  /** Unix second of the 1h close it was read on: the zone applies from here on. */
  fromSec: number;
  /** Price is in the zone now: the next 1h close decides. */
  armed: boolean;
  /** The engine's score needs a pattern trigger (hammer / engulfing), not just any close. */
  needsPattern?: boolean;
  /** The trade a signal here would get; null without a stop. */
  plan: SetupPlan | null;
  /** What the card explains, from the desk checklist. */
  details: SetupDetails;
}

/**
 * A signal's plan if it fired now, by the engine's rules (setupClassifier):
 * range fade → target 1 the range middle, target 2 near the far edge (exact);
 * trend pullback → target 1 the next resistance, which a trade needs at
 * ≥ 1.5× the risk (so 1.5× is its floor), target 2 at 2.5× the risk.
 */
export interface SetupPlan {
  /** Where it would enter: price now, held to the zone (the trigger candle's close decides). */
  entry: number;
  stop: number;
  tp1: number;
  /** false: tp1 is the lowest it can be (trend pullback). */
  tp1Exact: boolean;
  tp2: number;
  /** Reward to target 1 ÷ risk at `entry`. */
  rr: number;
  /** Where target 1 comes from, when the engine planned it (backend preTriggerChecks). */
  tp1Kind?: "range" | "structure" | "rr";
}

/**
 * A plan's reward in R (multiples of its risk): to target 1, to target 2, and
 * the whole trade if both hit (half closes at each, so the average of the two).
 * A trend pullback's 1.5R + 2.5R is 1 : 2.0.
 */
export function planReward(p: Pick<SetupPlan, "entry" | "stop" | "tp1" | "tp2">): { r1: number; r2: number; full: number } {
  const risk = Math.abs(p.entry - p.stop);
  if (risk <= 0) return { r1: 0, r2: 0, full: 0 };
  const r1 = Math.abs(p.tp1 - p.entry) / risk;
  const r2 = Math.abs(p.tp2 - p.entry) / risk;
  return { r1, r2, full: (r1 + r2) / 2 };
}

export interface SetupDetails {
  coin: string;
  archetype: "trend_pullback" | "range_fade";
  /** Price when the desk checked. */
  price: number;
  rulesMet: number;
  rulesTotal: number;
  /** The rules not met yet, in plain words: what the coin is waiting for. */
  waitingFor: string[];
  /** What to do now (desk verdict). */
  next: string;
}

// ─── outcome words ───────────────────────────────────────────────────────────

export type Tone = "profit" | "loss" | "neutral";

export const RESULT_META: Record<ChartSignalResult, { label: string; plain: string; done: boolean }> = {
  "full-win":  { label: "Won",       plain: "Both targets hit.",                                done: true },
  "be-stop":   { label: "TP1, then break-even", plain: "Target 1 hit, half booked. The rest closed at the entry price.", done: true },
  stopped:     { label: "Lost",      plain: "The stop-loss was hit.",                           done: true },
  "time-stop": { label: "Time limit", plain: "Closed because the plan's time limit ran out.",   done: true },
  closed:      { label: "Closed",    plain: "Closed outside the plan.",                         done: true },
  open:        { label: "Open",      plain: "Still in the trade: no stop or final target yet.", done: false },
  pending:     { label: "Waiting",   plain: "Price hasn't reached the entry yet.",              done: false },
  "no-fill":   { label: "No fill",   plain: "Price never reached the entry in time, so there was no trade.", done: true },
};

/** Short text for the tag next to BUY/SELL, e.g. "−1.0R", "open +0.4R", "now". */
export function resultTag(s: Pick<ChartSignal, "source" | "result" | "r">): string {
  if (s.source === "live" && !RESULT_META[s.result].done) return "now";
  if (s.result === "pending") return "waiting";
  if (s.result === "no-fill") return "no fill";
  const r = s.r === null ? "" : formatR(s.r);
  return s.result === "open" ? `open ${r}`.trim() : r;
}

/** Colour of a result: won/lost by its R, open trades too. */
export function resultTone(s: Pick<ChartSignal, "result" | "r">): Tone {
  if (s.r === null || s.result === "pending" || s.result === "no-fill") return "neutral";
  if (s.r > 0.05) return "profit";
  if (s.r < -0.05) return "loss";
  return "neutral";
}

/** "+1.8R" / "−1.0R" (true minus sign). */
export function formatR(r: number): string {
  const v = Math.abs(r).toFixed(1);
  return r > 0.05 ? `+${v}R` : r < -0.05 ? `−${v}R` : "0.0R";
}

// ─── merge with the desk ─────────────────────────────────────────────────────

/**
 * The desk's live check wins on the candle it just evaluated: its Ready plan
 * marks (or adds) that signal, and a "no trade" answer drops a replay on that
 * candle. The replay skips live-only filters (symbol cooldown, the full BTC
 * check), so on the newest candle the two can disagree; the desk is the one
 * the trader acts on.
 */
export function withDeskCheck(signals: ChartSignal[], desk: DeskCheck | null | undefined): ChartSignal[] {
  if (!desk) return signals;
  const { closeSec, plan } = desk;
  const onCandle = (s: ChartSignal) => s.signalTime === closeSec;
  if (!plan) return signals.filter(s => !(onCandle(s) && s.source === "replay"));

  const match = signals.find(s => s.action === plan.action && onCandle(s));
  if (match) return signals.map(s => (s === match && s.source === "replay" ? { ...s, ...plan, source: "live" as const } : s));
  const live: ChartSignal = {
    ...plan, id: `live-${closeSec}`, source: "live", signalTime: closeSec, result: "pending",
    fillTime: null, tp1Time: null, exitTime: null, exitPrice: null, r: null,
  };
  return [...signals.filter(s => !(onCandle(s) && s.source === "replay")), live];
}

// ─── legend summary ──────────────────────────────────────────────────────────

export interface SignalSummary {
  count: number; won: number; lost: number; even: number; open: number; noFill: number;
  /** Sum of closed trades' net R. */
  netR: number;
}

/** Counts for the signals that fired at or after `fromSec`; the parts add up to `count`. */
export function summarize(signals: ChartSignal[], fromSec: number): SignalSummary {
  const out: SignalSummary = { count: 0, won: 0, lost: 0, even: 0, open: 0, noFill: 0, netR: 0 };
  for (const s of signals) {
    if (s.signalTime < fromSec) continue;
    out.count++;
    if (s.result === "no-fill") out.noFill++;
    else if (!RESULT_META[s.result].done) out.open++;
    else {
      out.netR += s.r ?? 0;
      const tone = resultTone(s);
      if (tone === "profit") out.won++;
      else if (tone === "loss") out.lost++;
      else out.even++;
    }
  }
  out.netR = Math.round(out.netR * 100) / 100;
  return out;
}

// ─── geometry ────────────────────────────────────────────────────────────────

/**
 * Logical x position of a moment on the chart: bar i spans [i − 0.5, i + 0.5]
 * and starts at times[i]. Linear outside the data (crypto bars have no gaps),
 * so trades that run past the last candle or started before the first still
 * place correctly. `times` ascending, in seconds.
 */
export function logicalAt(times: number[], tfSec: number, t: number): number {
  const n = times.length;
  if (n === 0) return 0;
  let lo = 0, hi = n - 1;
  if (t <= times[0]!) hi = 0;
  else {
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (times[mid]! <= t) lo = mid;
      else hi = mid - 1;
    }
  }
  const i = Math.max(0, hi);
  return i - 0.5 + (t - times[i]!) / tfSec;
}

/** Index of the bar containing `t` (clamped to the data). */
export function barIndexAt(times: number[], tfSec: number, t: number): number {
  const n = times.length;
  return Math.min(n - 1, Math.max(0, Math.floor(logicalAt(times, tfSec, t) + 0.5)));
}

// ─── formatting ──────────────────────────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "1 Oct 04:00 UTC": the chart's time axis is UTC too. */
export function utcStamp(sec: number): string {
  const d = new Date(sec * 1000);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${hh}:${mm} UTC`;
}

/** "45 min" / "6 h" / "2 d 3 h". */
export function duration(sec: number): string {
  const m = Math.max(0, Math.round(sec / 60));
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}

// ─── data hook ───────────────────────────────────────────────────────────────

/** Open trades move: refetch this often (the backend caches each coin as long). */
const SIGNALS_REFRESH_MS = 5 * 60_000;

/**
 * One coin's signals (GET /api/trading-signals/chart/:symbol). Refetched when
 * the coin or timeframe changes and every 5 minutes; the backend caches each
 * coin for 5 minutes and never across a 1h close.
 */
/**
 * `at` (bar replay): the signals of a past window ending at `at.end` (unix
 * seconds), fetched once — a past window doesn't change.
 */
export function useChartSignals(symbol: string, refreshKey: string, enabled: boolean, at?: { end: number; days: number } | null) {
  // Signals belong to one coin and one window: a previous one's never draw.
  const key = at ? `${symbol}@${at.end}:${at.days}` : symbol;
  const [state, setState] = useState<{ key: string; data: ChartSignalsResponse | null; error: string | null }>(
    { key, data: null, error: null },
  );

  useEffect(() => {
    if (!enabled) return;
    let ctrl = new AbortController();
    setState(s => (s.key === key ? { ...s, error: null } : { key, data: null, error: null }));
    const url = at ? `/api/trading-signals/chart/${symbol}?end=${at.end}&days=${at.days}` : `/api/trading-signals/chart/${symbol}`;
    const load = () => {
      ctrl.abort();
      ctrl = new AbortController();
      fetch(url, { signal: ctrl.signal })
        .then(r => r.json() as Promise<ChartSignalsResponse>)
        .then(json => {
          if (!json.success) throw new Error(json.error || "Signals unavailable");
          setState({ key, data: json, error: null });
        })
        .catch(e => {
          if (e instanceof DOMException && e.name === "AbortError") return;
          setState(s => ({ ...s, key, error: e instanceof Error ? e.message : "Signals unavailable" }));
        });
    };
    load();
    const id = at ? 0 : setInterval(load, SIGNALS_REFRESH_MS);
    return () => {
      clearInterval(id);
      ctrl.abort();
    };
  }, [key, refreshKey, enabled]);

  const current = state.key === key ? state : { data: null, error: null };
  return { data: current.data, error: current.error, loading: enabled && !current.data && !current.error };
}
